import { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';

import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import offerFormDraftStorage from 'offer/helpers/offer-form-draft-storage';

// SSN/DOB/DL# reach the form from credit pulls; keep them out of the sessionStorage draft unless
// the agent actually edited them, and refill the dropped ones from the in-memory store on restore
const SENSITIVE_PERSON_FIELDS = ['ssn', 'dateOfBirth', 'driversLicenseNumber'];

const stripUneditedSensitiveFields = (people, storePeople) =>
  people.map((person) => {
    const storePerson = storePeople?.find((p) => p.id === person.id);
    if (!storePerson) {
      // Agent-added person: every field is their unsaved work
      return person;
    }
    const stripped = { ...person };
    SENSITIVE_PERSON_FIELDS.forEach((field) => {
      if (stripped[field] === storePerson[field]) {
        delete stripped[field];
      }
    });
    return stripped;
  });

const rehydrateSensitiveFields = (people, storePeople) =>
  people.map((person) => {
    const storePerson = storePeople?.find((p) => p.id === person.id);
    if (!storePerson) {
      return person;
    }
    const rehydrated = { ...person };
    SENSITIVE_PERSON_FIELDS.forEach((field) => {
      if (!(field in rehydrated) && field in storePerson) {
        rehydrated[field] = storePerson[field];
      }
    });
    return rehydrated;
  });

const mapPeopleFields = (values, store, mapper) => ({
  ...values,
  ...(values.drivers ? { drivers: mapper(values.drivers, store.offer?.quote?.drivers) } : {}),
  ...(values.nonDrivers ? { nonDrivers: mapper(values.nonDrivers, store.offer?.quote?.nonDrivers) } : {})
});

// Persists dirty offer form edits (and the active tab) to a per-offer sessionStorage draft so a
// forced re-auth doesn't lose the agent's work, and restores them once the offer has loaded.
// Mount with key={offerId}: the remount resets restoredRef per offer, and the restore gate holds
// saves until the new offer's draft decision so a stale form can't write under the new offer's key.
const OfferFormDraftSync = observer(({ offerId }) => {
  const { offer: store } = useStore();
  const { values, dirty, setValues } = useFormikContext();
  const { viewOnly } = useSession();
  const restoredRef = useRef(false);

  useEffect(() => {
    // Restore only after the offer has loaded — earlier, enableReinitialize would stomp the values
    if (viewOnly || restoredRef.current || store.loading || store?.offer?.id !== offerId) {
      return;
    }
    restoredRef.current = true;
    const draft = offerFormDraftStorage.getDraft(offerId);
    if (!draft?.values) {
      return;
    }
    // The form's blocks (home, autoCoverage, ...) are shaped for the store's type.
    // On load the store re-derives that type from the URL/quote, so a draft saved under another type
    // does not fit: an H draft has no autoCoverage, and the Auto tab would crash on it.
    // An unsaved type switch never survived a reload. Drop the draft instead of restoring it.
    // ponytail: edits made after an unsaved type switch are lost on re-auth (#35351). If that matters,
    // move the store to the draft's type first and restore after Formik reinitializes.
    if (draft.values.selectedOption && draft.values.selectedOption !== store.selectedOption) {
      return;
    }
    setValues(mapPeopleFields(draft.values, store, rehydrateSensitiveFields));
    if (draft.currentTab != null) {
      store.setCurrentTab(draft.currentTab);
    }
  }, [viewOnly, store.loading, store?.offer?.id, offerId, setValues, store.setCurrentTab, store.selectedOption]);

  useEffect(() => {
    if (viewOnly || !restoredRef.current) {
      return;
    }
    if (dirty) {
      offerFormDraftStorage.setDraft(offerId, {
        values: mapPeopleFields(values, store, stripUneditedSensitiveFields),
        currentTab: store.currentTab
      });
    } else {
      offerFormDraftStorage.clearDraft(offerId);
    }
  }, [values, dirty, viewOnly, offerId, store.currentTab]);

  return null;
});

export default OfferFormDraftSync;
